/**
 * A view's filters kept in the URL's search params, the one source of truth,
 * and remembered per view in `localStorage`, so returning to a view whose URL
 * carries none of them restores the last choice instead of the defaults.
 *
 * Every value is a string from a fixed set (or any string, for a field whose
 * spec lists no values), or for a list field an array of such strings; a URL
 * or stored value outside the set reads as the default. A list travels as the
 * router's own JSON array, so any string round-trips, separators included. Writes replace the history entry: flipping a filter is not a Back
 * stop.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';

export interface FieldSpec {
  default: string;
  /** The accepted values; unset accepts any string. */
  values?: readonly string[];
}

export interface ListFieldSpec {
  default: readonly string[];
  list: true;
  /** The accepted items; unset accepts any string. */
  values?: readonly string[];
}

export type ViewSpec = Record<string, FieldSpec | ListFieldSpec>;

export type ViewValues<S extends ViewSpec> = {
  [K in keyof S]: S[K] extends ListFieldSpec ? string[] : string;
};

type Value = string | readonly string[];

const keyFor = (view: string): string => `socialsrc.view.${view}`;

function readStored(view: string): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(keyFor(view));
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    // Storage disabled or a value that is not JSON: the defaults still work.
    return {};
  }
}

function writeStored(view: string, values: Record<string, Value>): void {
  try {
    localStorage.setItem(keyFor(view), JSON.stringify(values));
  } catch {
    // Quota exceeded or storage disabled: the URL still holds the choice.
  }
}

function acceptsItem(values: readonly string[] | undefined, value: unknown): value is string {
  return typeof value === 'string' && (values === undefined || values.includes(value));
}

function accepts(field: FieldSpec | ListFieldSpec, value: unknown): value is Value {
  if ('list' in field) {
    return Array.isArray(value) && value.every((item) => acceptsItem(field.values, item));
  }
  return acceptsItem(field.values, value);
}

const same = (a: Value, b: Value): boolean => JSON.stringify(a) === JSON.stringify(b);

export function useViewSearch<S extends ViewSpec>(
  view: string,
  spec: S,
): [ViewValues<S>, (patch: Partial<ViewValues<S>>) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const stored = readStored(view);

  const values = {} as Record<string, Value>;
  const missing: Record<string, Value> = {};
  for (const [name, field] of Object.entries(spec)) {
    const inUrl = search[name];
    if (accepts(field, inUrl)) {
      values[name] = inUrl;
    } else if (accepts(field, stored[name])) {
      values[name] = stored[name];
      if (!same(stored[name], field.default)) missing[name] = stored[name];
    } else {
      values[name] = field.default;
    }
  }

  // Restores a remembered choice into the URL once, so what the screen shows
  // and what the address bar says agree.
  const restore = JSON.stringify(missing);
  useEffect(() => {
    if (restore === '{}') return;
    void navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, ...JSON.parse(restore) }),
      replace: true,
    } as never);
  }, [restore, navigate]);

  const set = (patch: Partial<ViewValues<S>>): void => {
    writeStored(view, { ...values, ...(patch as Record<string, Value>) });
    void navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
      replace: true,
    } as never);
  };

  return [values as ViewValues<S>, set];
}
